import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { Tone } from './Alert.js';
import { cx } from './cx.js';

export interface ToastInput {
  tone?: Tone;
  title: string;
  description?: string;
  /** ms before it hides itself; 0 keeps it until dismissed. Default 5000. */
  duration?: number;
}

interface ToastItem extends Required<Omit<ToastInput, 'description'>> {
  id: number;
  description?: string;
}

interface ToastApi {
  show(toast: ToastInput): number;
  dismiss(id: number): void;
}

const ToastContext = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const nextId = useRef(1);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: number) => {
    const t = timers.current.get(id);
    if (t) clearTimeout(t);
    timers.current.delete(id);
    setToasts((list) => list.filter((x) => x.id !== id));
  }, []);

  const show = useCallback(
    (input: ToastInput) => {
      const id = nextId.current++;
      const item: ToastItem = {
        id,
        tone: input.tone ?? 'info',
        title: input.title,
        duration: input.duration ?? 5000,
        ...(input.description ? { description: input.description } : {}),
      };
      setToasts((list) => [...list.slice(-4), item]);
      if (item.duration > 0)
        timers.current.set(
          id,
          setTimeout(() => dismiss(id), item.duration),
        );
      return id;
    },
    [dismiss],
  );

  useEffect(() => {
    const map = timers.current;
    return () => map.forEach(clearTimeout);
  }, []);

  const api = useMemo(() => ({ show, dismiss }), [show, dismiss]);
  return (
    <ToastContext.Provider value={api}>
      {children}
      <section className="qk-toasts" aria-live="polite" aria-label="Notifications">
        {toasts.map((t) => (
          <div key={t.id} className={cx('qk-toast', `qk-toast--${t.tone}`)} role="status">
            <div className="qk-toast__body">
              <p className="qk-toast__title">{t.title}</p>
              {t.description ? <p className="qk-toast__text">{t.description}</p> : null}
            </div>
            <button
              type="button"
              className="qk-toast__close"
              onClick={() => dismiss(t.id)}
              aria-label="Dismiss notification"
            >
              ×
            </button>
          </div>
        ))}
      </section>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}
